import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Row,
  Col,
  Tag,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Spin,
  Alert,
  Divider,
} from 'antd';
import { PlusOutlined, GoogleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getScrapEmailsConfigDomains, saveScrapEmailsConfigDomains } from '../../../services/ReportsApi';

export interface SaveScrapEmailConfig {
    id?: number;
    email: string;
    encodedToken?: string;
    isActive?: boolean;
}

export interface ScrapEmailConfig {
  id: number;
  email: string;
  encodedToken?: string;
  scrappedError?: string;
  isActive?: boolean;
  lastScrapped: Date;
  createdAt: Date;
}

const GOOGLE_OAUTH_CONFIG = {
  clientId: process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID,
  clientSecret: process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_SECRET,
  scope: 'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/userinfo.email',
  responseType: 'code',
  redirectUri: typeof window !== 'undefined' ? `${window.location.origin}/reportes/email-config/oauth-callback` : '',
  authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
} as any;

const exchangeCodeForToken = async (code: string): Promise<any> => {
  try {
    const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        code: code,
        client_id: GOOGLE_OAUTH_CONFIG.clientId,
        client_secret: GOOGLE_OAUTH_CONFIG.clientSecret,
        redirect_uri: GOOGLE_OAUTH_CONFIG.redirectUri,
        grant_type: 'authorization_code',
      }),
    });

    if (!tokenResponse.ok) {
      const errorData = await tokenResponse.json();
      throw new Error(errorData.error_description || 'Error intercambiando código por token');
    }
    const result = await tokenResponse.json();
    console.log('Token response:', result);
    return result;
  } catch (error) {
    console.error('Error en exchangeCodeForToken:', error);
    throw error;
  }
};

const getUserEmailFromToken = async (accessToken: string): Promise<string> => {
  try {
    const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Error obteniendo información del usuario');
    }
    
    const userInfo = await response.json();
    return userInfo.email;
  } catch (error) {
    console.error('Error obteniendo email:', error);
    throw new Error('No se pudo obtener el email del usuario. Por favor ingrésalo manualmente.');
  }
};

const OAuthButton: React.FC<{
  onSuccess: (email: string, token: string) => void;
  onError: (error: string) => void;
}> = ({ onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);

  const handleGoogleAuth = async () => {
    setLoading(true);
    
    const authParams = new URLSearchParams({
      client_id: GOOGLE_OAUTH_CONFIG.clientId,
      redirect_uri: GOOGLE_OAUTH_CONFIG.redirectUri,
      response_type: GOOGLE_OAUTH_CONFIG.responseType,
      scope: GOOGLE_OAUTH_CONFIG.scope,
      state: btoa(JSON.stringify({ 
        timestamp: Date.now() 
      })),
      prompt: 'consent',
      access_type: 'offline'
    });

    const authWindow = window.open(
      `${GOOGLE_OAUTH_CONFIG.authUrl}?${authParams.toString()}`,
      'google_auth',
      'width=600,height=700,left=200,top=100'
    );

    if (!authWindow) {
      onError('Por favor permite ventanas emergentes para este sitio');
      setLoading(false);
      return;
    }

    const messageHandler = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        console.log('Mensaje de origen diferente:', event.origin);
        return;
      }
      
      if (event.data.type === 'OAUTH_SUCCESS') {
        try {
          const { code } = event.data;
          const tokenData = await exchangeCodeForToken(code);
          const userEmail = await getUserEmailFromToken(tokenData.access_token);
          const tokenToSave = {
            token: tokenData.access_token,
            refresh_token: tokenData.refresh_token,
            expires_in: tokenData.expires_in,
            token_type: tokenData.token_type,
            scope: [ 'https://www.googleapis.com/auth/gmail.readonly' ],
            timestamp: Date.now(),
            client_id: GOOGLE_OAUTH_CONFIG.clientId,
            client_secret: GOOGLE_OAUTH_CONFIG.clientSecret,
          };
          
          onSuccess(userEmail, JSON.stringify(tokenToSave));
        } catch (error) {
          console.error('Error en el proceso de autenticación:', error);
          onError(error instanceof Error ? error.message : 'Error en el proceso de autenticación');
        } finally {
          setLoading(false);
          authWindow.close();
          window.removeEventListener('message', messageHandler);
        }
      }
      
      if (event.data.type === 'OAUTH_ERROR') {
        console.error('Error de OAuth:', event.data.error);
        onError(`Error de autenticación: ${event.data.error}`);
        setLoading(false);
        authWindow.close();
        window.removeEventListener('message', messageHandler);
      }
    };

    window.addEventListener('message', messageHandler);
    
    const checkWindow = setInterval(() => {
      if (authWindow.closed) {
        console.log('Ventana cerrada por el usuario');
        setLoading(false);
        clearInterval(checkWindow);
        window.removeEventListener('message', messageHandler);
      }
    }, 1000);

    setTimeout(() => {
      if (!authWindow.closed) {
        authWindow.close();
        setLoading(false);
        window.removeEventListener('message', messageHandler);
        onError('Tiempo de autenticación agotado');
      }
      clearInterval(checkWindow);
    }, 10 * 60 * 1000);
  };

  return (
    <div>
      <Button
        type="primary"
        icon={<GoogleOutlined />}
        loading={loading}
        onClick={handleGoogleAuth}
        style={{ marginBottom: 8, width: '100%' }}
        size="large"
      >
        {loading ? 'Autenticando...' : 'Autenticar con Google'}
      </Button>
    </div>
  );
};

const EmailConfigModal: React.FC<{
    visible: boolean;
    onClose: () => void;
  }> = ({ visible, onClose }) => {
    const [configs, setConfigs] = useState<ScrapEmailConfig[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingConfig, setEditingConfig] = useState<SaveScrapEmailConfig | null>(null);
    const [useManualToken, setUseManualToken] = useState(false);
    const [oauthLoading, setOauthLoading] = useState(false);
    const [form] = Form.useForm();

    const loadConfigs = async () => {
      setLoading(true);
      try {
        const data = await getScrapEmailsConfigDomains();
        setConfigs(data);
      } catch (error) {
        message.error('Error cargando la configuración.');
      } finally {
        setLoading(false);
      }
    };

    const handleSaveConfig = async (values: any) => {
      try {
        await saveScrapEmailsConfigDomains({
            id: editingConfig?.id, 
            ...values
        });
        message.success('Cambios realizados con éxito');
        setEditingConfig(null);
        setUseManualToken(false);
        form.resetFields();
        loadConfigs();
      } catch (error) {
        message.error('Error salvando los cambios');
      }
    };

    const validateToken = (token: string): boolean => {
      try {
        JSON.parse(token);
        return true;
      } catch {
        return false;
      }
    };

    const handleOAuthSuccess = async (email: string, token: string) => {
      setOauthLoading(true);
      try {
        await saveScrapEmailsConfigDomains({
          email: email,
          encodedToken: token,
          isActive: true
        });
        
        message.success(`Configuración guardada exitosamente para: ${email}`);
        setEditingConfig(null);
        setUseManualToken(false);
        form.resetFields();
        loadConfigs();
      } catch (error) {
        message.error('Error guardando la configuración');
      } finally {
        setOauthLoading(false);
      }
    };

    const handleOAuthError = (error: string) => {
      message.error(error);
    };

    const handleClose = () => {
      setEditingConfig(null);
      setUseManualToken(false);
      form.resetFields();
      onClose();
    };

    useEffect(() => {
      if (visible) {
        loadConfigs();
      }
    }, [visible]);

    return (
      <Modal
        title="Emails configurados"
        open={visible}
        onCancel={handleClose}
        footer={null}
        width={800}
      >
        <Spin spinning={loading}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setEditingConfig({
              email: '',
              isActive: true,
            })}
            style={{ marginBottom: 16 }}
          >
            Agregar nuevo email
          </Button>

          <Modal
            title={editingConfig?.id ? 'Editar configuración' : 'Agregar nueva configuración'}
            open={!!editingConfig}
            onCancel={() => {
              setEditingConfig(null);
              setUseManualToken(false);
              form.resetFields();
            }}
            onOk={() => form.submit()}
            width={600}
            okText={editingConfig?.id ? 'Actualizar' : 'Guardar'}
            cancelText="Cancelar"
            confirmLoading={oauthLoading}
          >
            <Spin spinning={oauthLoading}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSaveConfig}
                initialValues={editingConfig || { isActive: true }}
              >
                {useManualToken && (
                  <>
                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[{ required: true, type: 'email', message: 'Por favor ingresa un email válido' }]}
                    >
                      <Input 
                        placeholder="ejemplo@gmail.com" 
                        disabled={!!editingConfig?.id}
                      />
                    </Form.Item>
                    
                    <Divider />
                  </>
                )}
                
                <Form.Item label="Método de autenticación">
                  <div style={{ marginBottom: 16 }}>
                    <Switch
                      checked={useManualToken}
                      onChange={setUseManualToken}
                      checkedChildren="Manual"
                      unCheckedChildren="Automático"
                    />
                    <span style={{ marginLeft: 8 }}>
                      {useManualToken ? 'Ingresar token manualmente' : 'Autenticación con Google'}
                    </span>
                  </div>

                  {!useManualToken ? (
                    <OAuthButton
                      onSuccess={handleOAuthSuccess}
                      onError={handleOAuthError}
                    />
                  ) : (
                    <div>
                      <Alert
                        message="Ingreso manual de token"
                        description="Ingresa el token JSON manualmente. Asegúrate de que tenga los permisos necesarios para Gmail."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                      <Form.Item
                        name="encodedToken"
                        label="Token JSON"
                        rules={[
                          { required: true, message: 'Por favor ingresa el token' },
                          {
                            validator: (_, value) => {
                              if (!value || validateToken(value)) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error('El token debe ser un JSON válido'));
                            }
                          }
                        ]}
                      >
                        <Input.TextArea 
                          rows={6} 
                          placeholder={`Pega aquí el token JSON con formato:
                            {
                              "token": "ya29.a0Af...",
                              "refresh_token": "1//0egV...", 
                              "expires_in": 3600,
                              "scope": "https://www.googleapis.com/auth/gmail.readonly",
                              "token_type": "Bearer"
                            }`
                          }
                          style={{ fontFamily: 'monospace', fontSize: '12px' }}
                        />
                      </Form.Item>
                    </div>
                  )}
                </Form.Item>

                {useManualToken && (
                  <>
                    <Divider />
                    <Form.Item 
                      name="isActive" 
                      label="Estado" 
                      valuePropName="checked"
                      tooltip="Activar o desactivar esta configuración"
                    >
                      <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
                    </Form.Item>
                  </>
                )}
              </Form>
            </Spin>
          </Modal>

          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {configs.map(config => (
              <Card key={config.id} size="small" style={{ marginBottom: 8 }}>
                <Row align="middle" gutter={16}>
                  <Col flex={1}>
                    <div>
                      <strong>{config.email}</strong>
                      {config.scrappedError && (
                        <Alert
                          message={config.scrappedError}
                          type="error"
                          showIcon
                          style={{ marginTop: 8 }}
                        />
                      )}
                      <div style={{ marginTop: 4 }}>
                        <Tag color={config.isActive ? 'green' : 'red'}>
                          {config.isActive ? 'Activo' : 'Inactivo'}
                        </Tag>
                        <span style={{ color: '#999', fontSize: 12 }}>
                          Última ejecución: {config.lastScrapped ? dayjs(config.lastScrapped).format('DD/MM/YYYY HH:mm') : 'No ejecutado'}
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <Button
                      type="link"
                      onClick={() => {
                        setEditingConfig(config);
                        setUseManualToken(true);
                      }}
                    >
                      Editar
                    </Button>
                  </Col>
                </Row>
              </Card>
            ))}
          </div>
        </Spin>
      </Modal>
    );
  };

export default EmailConfigModal;